import { useState } from 'react'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { AlertCircle, CheckCircle, Loader2, FileText } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { DescriptionPromptDialog } from './DescriptionPromptDialog'

interface Book {
  id: string
  title_international: string
  author_international: string
  description?: string | null
}

interface GenerateResult {
  book_id: string
  title: string
  success: boolean
  description?: string
  error?: string
  generating?: boolean
}

interface GenerateDescriptionDialogProps {
  books: Book[]
  onClose: () => void
  onSuccess: () => void
}

export function GenerateDescriptionDialog({ books, onClose, onSuccess }: GenerateDescriptionDialogProps) {
  const [results, setResults] = useState<GenerateResult[]>([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [showPromptDialog, setShowPromptDialog] = useState(true)

  // Get first book's info for prompt template selection (template will be applied to all)
  const firstBook = books[0]
  const promptTitle = firstBook?.title_international || 'Book Title'
  const promptAuthor = firstBook?.author_international || 'Author Name'

  const startGeneration = async (template: string) => {
    setShowPromptDialog(false)
    
    // Initialize results with all books as generating (show spinners)
    const initialResults: GenerateResult[] = books.map(book => ({
      book_id: book.id,
      title: book.title_international,
      success: false,
      generating: true, // Start with generating=true to show spinners
      error: undefined,
    }))
    setResults(initialResults)
    setIsGenerating(true)

    try {
      // Call bulk API to generate all descriptions (happens on backend)
      // Meanwhile, UI shows spinners for all books
      const bookIds = books.map(b => b.id)
      const response = await apiClient.bulkGenerateDescriptions(bookIds, template)
      
      if (response.success && response.results) {
        // Map the results back to our state structure
        const updatedResults = response.results.map(result => ({
          book_id: result.book_id,
          title: result.title,
          success: result.success,
          description: result.description,
          error: result.error,
          generating: false // Stop showing spinner
        }))
        
        setResults(updatedResults)
      } else {
        // If the API call failed, mark all as failed
        setResults(initialResults.map(r => ({
          ...r,
          success: false,
          generating: false,
          error: 'Failed to generate descriptions'
        })))
      }
    } catch (error: any) {
      // On error, mark all as failed
      setResults(initialResults.map(r => ({
        ...r,
        success: false,
        generating: false,
        error: error.message || 'Failed to generate descriptions'
      })))
    }

    setIsGenerating(false)
    setIsComplete(true)
  }

  const successCount = results.filter(r => r.success).length
  const failedCount = results.filter(r => r.error !== undefined).length
  const pendingCount = results.filter(r => r.generating || (!r.success && r.error === undefined)).length

  // Show prompt selection dialog first
  if (showPromptDialog) {
    return (
      <DescriptionPromptDialog
        open={true}
        onClose={onClose}
        onConfirm={startGeneration}
        title={promptTitle}
        author={promptAuthor}
      />
    )
  }

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className="generate-description-dialog max-w-2xl max-h-[80vh] overflow-hidden flex flex-col [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Generate AI Descriptions
          </DialogTitle>
          <DialogDescription>
            {isGenerating 
              ? `Generating descriptions for ${books.length} book${books.length > 1 ? 's' : ''}...`
              : `Generated ${successCount} of ${books.length} description${books.length > 1 ? 's' : ''}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Progress Summary */}
        <div className="progress-summary grid grid-cols-3 gap-4 py-4">
          <div className="success-count flex flex-col items-center p-3 bg-green-50 dark:bg-green-950 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mb-1" />
            <span className="text-2xl font-bold text-green-600 dark:text-green-400">{successCount}</span>
            <span className="text-xs text-muted-foreground">Success</span>
          </div>
          <div className="failed-count flex flex-col items-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mb-1" />
            <span className="text-2xl font-bold text-red-600 dark:text-red-400">{failedCount}</span>
            <span className="text-xs text-muted-foreground">Failed</span>
          </div>
          <div className="pending-count flex flex-col items-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mb-1" />
            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">{pendingCount}</span>
            <span className="text-xs text-muted-foreground">Pending</span>
          </div>
        </div>

        {/* Results List */}
        <div className="results-list flex-1 overflow-y-auto space-y-2 border rounded-lg p-4">
          {results.map((result) => (
            <div
              key={result.book_id}
              className={`result-item flex items-start gap-3 p-3 rounded-lg border ${
                result.generating
                  ? 'bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'
                  : result.success
                  ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
                  : result.error
                  ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
                  : 'bg-muted border-border'
              }`}
            >
              <div className="result-icon mt-1">
                {result.generating ? (
                  <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : result.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                ) : result.error ? (
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
                )}
              </div>

              <div className="result-content flex-1 min-w-0">
                <div className="result-title font-medium text-sm truncate">
                  {result.title}
                </div>
                {result.generating && (
                  <div className="result-status text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Generating description...
                  </div>
                )}
                {result.success && result.description && (
                  <div className="result-description text-xs text-muted-foreground mt-1 line-clamp-2">
                    {result.description}
                  </div>
                )}
                {result.error && (
                  <div className="result-error text-xs text-red-600 dark:text-red-400 mt-1">
                    {result.error}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            onClick={() => {
              onSuccess()
              onClose()
            }}
            disabled={isGenerating}
            variant={isComplete ? 'default' : 'outline'}
          >
            {isComplete ? 'Done' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

